import app from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import 'firebase/storage';

const prodConfig = {
  apiKey: process.env.REACT_APP_PROD_API_KEY,
  authDomain: process.env.REACT_APP_PROD_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_PROD_DATABASE_URL,
  projectId: process.env.REACT_APP_PROD_PROJECT_ID,
  storageBucket: process.env.REACT_APP_PROD_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_PROD_MESSAGING_SENDER_ID,
};

const devConfig = {
  apiKey: process.env.REACT_APP_DEV_API_KEY,
  authDomain: process.env.REACT_APP_DEV_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DEV_DATABASE_URL,
  projectId: process.env.REACT_APP_DEV_PROJECT_ID,
  storageBucket: process.env.REACT_APP_DEV_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_DEV_MESSAGING_SENDER_ID,
}

const config = process.env.NODE_ENV === 'production' ? prodConfig : devConfig;

class Firebase {
  constructor() {
    app.initializeApp(config);

    this.auth = app.auth();
    this.db = app.database();
    this.storage = app.storage();
  }

  // *** AUTH API ***

  doCreateUserWithEmailAndPassword = (email, password) =>
  this.auth.createUserWithEmailAndPassword(email, password);

  createTemporaryPassword = () =>
    `Sq${Math.random().toString(36).slice(-10)}A1!`;

  doSignInWithEmailAndPassword = (email, password) =>
  this.auth.signInWithEmailAndPassword(email, password);

  doSignOut = () => this.auth.signOut();

  doPasswordReset = email => this.auth.sendPasswordResetEmail(email);

  doPasswordUpdate = password => this.auth.currentUser.updatePassword(password);

  doSendEmailVerification = () => this.auth.currentUser.sendEmailVerification({
    url: 'https://portal.sqoot.nl/',
  });

  // *** Merge Auth and DB User API ***

  onAuthUserListener = (next, fallback) =>
    this.auth.onAuthStateChanged(authUser => {
      if (authUser) {
        this.user(authUser.uid)
          .once('value')
          .then(snapshot => {
            const dbUser = snapshot.val();

            //default empty roles
            if(!dbUser.roles) {
              dbUser.roles = {};
            }

            //merge auth and db user
            authUser = {
              uid: authUser.uid,
              email: authUser.email,
              emailVerified: authUser.emailVerified,
              providerData: authUser.providerData,
              ...dbUser,
            };

            next(authUser);
          });
      } else {
        fallback();
      }
    });

    // *** USER API ***

  authUser = uid => this.auth.ref(`users/${uid}`);


  // *** USER API ***

  user = uid => this.db.ref(`users/${uid}`);

  users = () => this.db.ref('users');

  snapshotToList = (snapshot) => {
    const value = snapshot.val();
    if (!value) {
      return [];
    }
    return Object.keys(value).map(key => ({
      ...value[key],
      uid: key,
    }));
  }

  usersByCompany = (companyName) =>
    this.db.ref('users').orderByChild('company').equalTo(companyName);

  usersByDealer = (dealerName) =>
    this.db.ref('users').orderByChild('dealer').equalTo(dealerName);

  usersByKeyPrefix = (prefix) =>
    this.db.ref('users').orderByKey().startAt(prefix).endAt(`${prefix}\uf8ff`).limitToFirst(20);

  // *** COMPANIES API ***

  company = uid => this.db.ref(`companies/${uid}`);

  companyObject = (uid, objectUid) => this.db.ref(`companies/${uid}/objecten/${objectUid}`)

  companyObjects = uid => this.db.ref(`companies/${uid}/objecten`)

  companies = () => this.db.ref('companies');

    // *** COMPANIES API ***

  dealer = uid => this.db.ref(`dealers/${uid}`);

  dealers = () => this.db.ref('dealers');

  // *** OBJECTS API ***

  object = uid => this.db.ref(`objects/${uid}`);

  objects = () => this.db.ref('objects');

  objectsByCompany = (companyName) =>
    this.db.ref('objects').orderByChild('company').equalTo(companyName);

  objectsByDealer = (dealerName) =>
    this.db.ref('objects').orderByChild('dealer').equalTo(dealerName);

  schademelding = (uid, meldingUid) => this.db.ref(`objects/${uid}/schademeldingen/${meldingUid}`)

  schademeldingen = (uid) => this.db.ref(`objects/${uid}/schademeldingen`)

  serviceaanvraag = (uid, aanvraagUid) => this.db.ref(`objects/${uid}/serviceaanvragen/${aanvraagUid}`)

  serviceaanvragen = (uid) => this.db.ref(`objects/${uid}/schademeldingen`)

  diefstalmelding = (uid, meldingUid) => this.db.ref(`objects/${uid}/diefstallen/${meldingUid}`)

  diefstalmeldingen = (uid) => this.db.ref(`objects/${uid}/diefstallen`)


  // *** USER STORAGE API ***

  userStorage = (uid) => this.storage.ref(`users/${uid}`);

  sanitizeFileName = (name) => {
    const base = String(name || 'upload').split(/[/\\]/).pop();
    return base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
  }

  assertAllowedUpload = (file) => {
    const maxBytes = 10 * 1024 * 1024;
    const allowedTypes = {
      'application/pdf': true,
      'image/jpeg': true,
      'image/png': true,
      'image/webp': true,
      'image/gif': true,
      'image/heic': true,
      'application/msword': true,
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': true,
      'application/vnd.ms-excel': true,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': true,
    };
    const allowedExt = /\.(pdf|jpe?g|png|webp|gif|heic|docx?|xlsx?)$/i;

    if (!file) {
      return Promise.reject(new Error('Geen bestand geselecteerd.'));
    }
    if (file.size > maxBytes) {
      return Promise.reject(new Error('Bestand is groter dan 10 MB.'));
    }
    const typeOk = !file.type || allowedTypes[file.type];
    const nameOk = allowedExt.test(file.name || '');
    if (!typeOk && !nameOk) {
      return Promise.reject(new Error('Dit bestandstype is niet toegestaan.'));
    }
    return null;
  }

  guessContentType = (name) => {
    const lower = String(name || '').toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.heic')) return 'image/heic';
    if (lower.endsWith('.doc')) return 'application/msword';
    if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (lower.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    return '';
  }

  putUpload = (folder, file, date) => {
    const invalid = this.assertAllowedUpload(file);
    if (invalid) {
      return invalid;
    }
    const safeName = this.sanitizeFileName(file.name);
    const contentType = file.type || this.guessContentType(file.name);
    return this.storage.ref(`${folder}${safeName}-${date}`).put(file, {
      contentType,
    });
  }

  // *** INVOICES API ***

  invoice = (file, uid, date) => this.putUpload(`users/${uid}/invoices/`, file, date);

  invoices = () => this.storage.ref("invoices");

  // *** LEASECONTRACT API ***

  leaseContract = (file, uid, date) => this.putUpload(`users/${uid}/leasecontract/`, file, date);

  leaseContracts = () => this.storage.ref("leasecontract");

  // *** MAINTENANCE API ***

  maintenance = (file, uid, date) => this.putUpload(`objects/${uid}/maintenance/`, file, date);

  maintenances = uid => this.storage.ref(`objects/${uid}/maintenance`);

  // *** SCHADEMELDING API ***

  schade = (file, uid, date) => this.putUpload(`objects/${uid}/schademelding/`, file, date);

  schades = uid => this.storage.ref(`objects/${uid}/schademelding`);

  // *** DIEFSTALMELDING API ***

  diefstal = (file, uid, date) => this.putUpload(`objects/${uid}/diefstal/`, file, date);

  diefstallen = uid => this.storage.ref(`objects/${uid}/diefstal`);

  // *** COMPANY INVOICES API ***

  companyInvoice = (file, uid, date) => this.putUpload(`companies/${uid}/invoices/`, file, date);

  companyInvoices = uid => this.storage.ref(`companies/${uid}/invoices`);

  // *** COMPANY RAAMCONTRACT API ***

  companyRaamContract = (file, uid, date) => this.putUpload(`companies/${uid}/raamcontract/`, file, date);

  companyRaamContracten = uid => this.storage.ref(`companies/${uid}/raamcontract`);

  // maintenances = () => this.storage.ref('maintenance');

  // *** OBJECT LEASECONTRACT API ***

  leaseContractObject = (file, uid, date) => this.putUpload(`objects/${uid}/leasecontract/`, file, date);

  leaseContractObjects = uid => this.storage.ref(`objects/${uid}/leasecontract`);

  // *** MESSAGES API ***

  // message = uid => this.db.ref(`messages/${uid}`);
  //
  // messages = () => this.db.ref('messages');

}





export default Firebase;
